/* eslint-disable react-refresh/only-export-components -- small shared admin kit */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'
import { admin } from '../../api/client.js'
import { useAuth } from '../../auth.jsx'

export const inputClass =
  'h-11 w-full rounded-xl border-[1.5px] border-edge bg-surface px-3 text-base font-medium text-ink outline-none focus:border-teal'

export const btn = {
  primary:
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-teal px-4 py-2.5 font-extrabold text-on-teal transition hover:bg-teal-dark disabled:cursor-not-allowed disabled:opacity-50',
  dark: 'inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-ink px-4 py-2.5 font-extrabold text-on-ink transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50',
  ghost:
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border-[1.5px] border-edge bg-surface px-4 py-2.5 font-extrabold text-ink transition hover:border-ink disabled:cursor-not-allowed disabled:opacity-50',
  danger:
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border-[1.5px] border-live px-4 py-2.5 font-extrabold text-live transition hover:bg-live hover:text-white',
}

export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-1.5 text-sm font-extrabold ${className}`}>
      {label}
      {children}
      {hint && <span className="text-xs font-semibold text-subtle">{hint}</span>}
    </label>
  )
}

export function Section({ title, children, actions }) {
  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-line p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-black">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  )
}

export function Notice({ status }) {
  if (!status) return null
  return (
    <p role="status" className={`text-sm font-bold ${status.ok ? 'text-teal' : 'text-live'}`}>
      {status.text}
    </p>
  )
}

/** Run an async action and turn its result into a status message. */
export function useAction() {
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(false)
  const run = async (fn, okText) => {
    setBusy(true)
    setStatus(null)
    try {
      const result = await fn()
      if (okText) setStatus({ ok: true, text: okText })
      return result
    } catch (e) {
      setStatus({ ok: false, text: e.message })
      return undefined
    } finally {
      setBusy(false)
    }
  }
  return { status, busy, run, setStatus }
}

export function RequireAdmin({ children }) {
  const { t } = useTranslation()
  const { user, ready, profile, isAdmin } = useAuth()
  if (!ready) return null
  if (!user) return <Navigate to="/login" state={{ from: window.location.pathname }} replace />
  if (!profile) return <p className="px-8 py-24 text-center text-subtle">{t('common.loading')}</p>
  if (!isAdmin) return <p className="px-8 py-24 text-center text-lg font-bold text-muted">{t('admin.notAllowed')}</p>
  return children
}

export function PhotoUpload({ value, onChange }) {
  const { t } = useTranslation()
  const { status, busy, run } = useAction()
  const onFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const res = await run(() => admin.upload(file))
    if (res) onChange(res.url)
    e.target.value = ''
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      {value ? (
        <img src={value} alt="" className="size-20 rounded-xl object-cover" />
      ) : (
        <div className="size-20 rounded-xl bg-paper" />
      )}
      <label className={`${btn.ghost} ${busy ? 'opacity-50' : ''}`}>
        {t('admin.form.upload')}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={onFile} />
      </label>
      <Notice status={status} />
    </div>
  )
}

export function SecretsBox({ provisioning, warnings = [] }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  if (!provisioning && !warnings.length) return null
  const download = () => {
    const blob = new Blob([provisioning.secrets_h], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'secrets.h'
    a.click()
    URL.revokeObjectURL(a.href)
  }
  return (
    <div className="flex flex-col gap-3 rounded-2xl border-2 border-teal bg-mint/40 p-5">
      {provisioning && (
        <>
          <h3 className="text-lg font-black">{t('admin.prov.title')}</h3>
          <p className="text-sm text-muted">{t('admin.prov.text')}</p>
          <pre className="overflow-x-auto rounded-xl bg-ink p-4 text-[13px] leading-relaxed text-on-ink">
            {provisioning.secrets_h}
          </pre>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btn.primary} onClick={download}>
              {t('admin.prov.download')}
            </button>
            <button
              type="button"
              className={btn.ghost}
              onClick={() => navigator.clipboard.writeText(provisioning.secrets_h).then(() => setCopied(true))}
            >
              {copied ? t('admin.prov.copied') : t('admin.prov.copy')}
            </button>
          </div>
        </>
      )}
      {warnings.length > 0 && (
        <div className="text-sm">
          <strong>{t('admin.prov.warnings')}:</strong>
          <ul className="list-disc pl-5 text-live">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
